<template>
  <div class="product-page">
    <div v-if="loading" class="loading-state">
      <div class="spinner"></div>
      <p>Đang tải...</p>
    </div>
    <!-- Header -->
    <div class="product-header">
      <h1>Quản lý Sản phẩm trong Kho kĩ thuật</h1>
      <div class="button-action">
        <button @click="viewModalCreateProduct" class="btn-add">
          + Thêm sản phẩm
        </button>
        <button @click="viewModalExportReceipt" class="btn-add">
          + Tạo phiếu xuất kho LK
        </button>
        <button @click="viewModalTrashProduct" class="btn-trash">
          <i class="fa fa-trash"></i> Thùng rác
        </button>
      </div>
    </div>

    <!-- Filter -->
    <div class="product-filter">
      <input v-model="search" type="text" placeholder="Tìm kiếm sản phẩm..." />
      <select v-model="filter.loai_hang" @change="fetchProducts">
        <option value="">Tất cả danh mục</option>
        <option value="Linh kiện">Linh kiện</option>
        <option value="Hàng hoá">Hàng hoá</option>
        <option value="iPad">iPad</option>
      </select>

      <button @click="fetchProducts" class="btn-search">Tìm kiếm</button>
      <div class="column-toggle">
        <button @click="showColumnPanel = !showColumnPanel">
          ⚙️ Cột hiển thị
        </button>

        <div v-if="showColumnPanel" class="column-panel">
          <label v-for="col in columns" :key="col.key">
            <input type="checkbox" v-model="col.visible" />
            {{ col.label }}
          </label>
        </div>
      </div>
      <button @click="triggerFileInput" class="btn-export">
        <i class="fas fa-file-import"></i> Import file
        <input
          type="file"
          ref="fileInput"
          style="display: none"
          accept=".xlsx, .xls, .csv"
          @change="handleFileImport"
        />
      </button>
      <button @click="exportExcel" class="btn-export">
        <i class="fas fa-file-download"></i> Xuất file
      </button>
    </div>

    <!-- Table -->
    <div class="product-table">
      <table>
        <thead>
          <tr>
            <th>#</th>
            <th v-for="col in visibleColumns" :key="col.key">
              {{ col.label }}
            </th>
            <th>Hành động</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(item, index) in products" :key="item.id">
            <td>{{ index + 1 }}</td>
            <td v-for="col in visibleColumns" :key="col.key">
              <template v-if="col.key === 'hinh_anh'">
                <img
                  :src="getAvatarUrl(item[col.key])"
                  alt="Product Image"
                  class="product_image"
                />
              </template>
              <template v-else>
                {{ renderValue(item, col) }}
              </template>
            </td>
            <td>
              <button
                @click="approveToShelf(item.id)"
                class="btn-icon btn-shelf"
                title="Phê duyệt lên kệ"
              >
                <i class="fas fa-check"></i>
              </button>
              <button
                @click="viewModalDetailProduct(item)"
                class="btn-icon btn-edit"
                title="Xem & Sửa"
              >
                <i class="fas fa-edit"></i>
              </button>
              <button
                @click="deleteProduct(item)"
                class="btn-icon btn-delete"
                title="Xóa"
              >
                <i class="fas fa-trash-alt"></i>
              </button>
            </td>
          </tr>
          <tr v-if="products.length === 0">
            <td colspan="100%" class="empty">Không có dữ liệu</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Pagination -->
    <div v-if="pagination.total > 0" class="pagination">
      <div class="pagination-info">
        Hiển thị {{ pagination.from }} - {{ pagination.to }} trong tổng số
        {{ pagination.total }} sản phẩm
      </div>
      <div class="pagination-controls">
        <button
          @click="changePage(pagination.current_page - 1)"
          :disabled="pagination.current_page === 1"
          class="btn btn-outline btn-sm"
        >
          Trước
        </button>

        <button
          v-for="page in visiblePages"
          :key="page"
          @click="changePage(page)"
          :class="[
            'btn',
            'btn-sm',
            page === pagination.current_page ? 'btn-primary' : 'btn-outline',
          ]"
        >
          {{ page }}
        </button>

        <button
          @click="changePage(pagination.current_page + 1)"
          :disabled="pagination.current_page === pagination.last_page"
          class="btn btn-outline btn-sm"
        >
          Sau
        </button>
      </div>
    </div>
    <CreateProductModal
      v-if="showCreateModal"
      @close="showCreateModal = false"
      @save="saveProduct"
    />
    <ProductDetailModal
      v-if="showDetailModal"
      :product="selectedProduct"
      @close="showDetailModal = false"
      @save="saveProduct"
    />
    <ExportReceiptModal
      v-if="showExportReceiptModal"
      @close="showExportReceiptModal = false"
      @save="saveExportReceipt"
    />
    <ConfirmDialog
      v-if="showConfirmDialog"
      :title="confirmDialog.title"
      :message="confirmDialog.message"
      :type="confirmDialog.type"
      @confirm="confirmDialog.onConfirm"
      @cancel="showConfirmDialog = false"
    />

    <TrashProductModal
      v-if="showModalTrashProduct"
      @close="showModalTrashProduct = false"
      @save="fetchProducts"
    />
  </div>
</template>

<script setup>
import ConfirmDialog from "@/components/modal/ConfirmDialog.vue";
import CreateProductModal from "@/components/modal/products/CreateProductModal.vue";
import ExportReceiptModal from "@/components/modal/exportReceipt/ExportReceiptModal.vue";
import { usePagination } from "@/composables/usePagination";
import { useToast } from "@/composables/useToast";
import { importApi, productApi } from "@/services/api";
import { formatCurrency, getAvatarUrl } from "@/utils/helpers";
import { computed, onMounted, reactive, ref } from "vue";
import ProductDetailModal from "@/components/modal/products/ProductDetailModal.vue";
import TrashProductModal from "@/components/modal/products/TrashProductModal.vue";

const fileInput = ref(null);
const toast = useToast();
const showColumnPanel = ref(false);
const products = ref([]);
const loading = ref(false);
const search = ref("");
const selectedProduct = ref(null);
const showConfirmDialog = ref(false);
const showCreateModal = ref(false);
const showExportReceiptModal = ref(false);
const showDetailModal = ref(false);
const showModalTrashProduct = ref(false);

const filter = reactive({
  loai_hang: "",
  kho_ki_thuat: 1,
});

const confirmDialog = reactive({
  title: "",
  message: "",
  type: "danger",
  onConfirm: () => {},
});

const columns = ref([
  { key: "loai_hang", label: "Loại hàng", visible: false },
  { key: "nhom_hang", label: "Nhóm hàng", visible: false },
  { key: "ma_hang", label: "Mã hàng", visible: true },
  { key: "ma_vach", label: "Mã vạch", visible: false },
  { key: "ten_hang", label: "Tên sản phẩm", visible: true },
  { key: "thuong_hieu", label: "Thương hiệu", visible: false },
  { key: "gia_ban", label: "Giá bán", visible: true, type: "currency" },
  { key: "gia_von", label: "Giá vốn ban đầu", visible: true, type: "currency" },
  {
    key: "gia_von_linh_kien",
    label: "Giá vốn linh kiện",
    visible: true,
    type: "currency",
  },
  {
    key: "tong_gia_von",
    label: "Tổng giá vốn",
    visible: true,
    type: "currency",
  },
  { key: "ton_kho", label: "Tồn kho", visible: true },
  { key: "kh_dat", label: "Khách đặt", visible: false },
  { key: "du_kien_het_hang", label: "Dự kiến hết hàng", visible: false },
  { key: "ton_nho_nhat", label: "Tồn nhỏ nhất", visible: false },
  { key: "ton_lon_nhat", label: "Tồn lớn nhất", visible: false },
  { key: "loai_khach", label: "Loại khách", visible: false },
  { key: "dvt", label: "Đơn vị tính", visible: false },
  { key: "ma_dvt_co_ban", label: "Mã đơn vị tính cơ bản", visible: false },
  { key: "quy_doi", label: "Quy đổi", visible: false },
  { key: "thuoc_tinh", label: "Thuộc tính", visible: false },
  { key: "ma_hh_lien_quan", label: "Mã hàng hoá liên quan", visible: false },
  { key: "hinh_anh", label: "Hình ảnh", visible: false },
  { key: "su_dung_imei", label: "Sử dụng imei", visible: false },
  { key: "trong_luong", label: "Trọng lượng", visible: false },
  { key: "serial_imei", label: "Serial Imei", visible: true },
  { key: "dang_kinh_doanh", label: "Đang kinh doanh", visible: false },
  { key: "duoc_ban_truc_tiep", label: "Được bán trực tiếp", visible: false },
  { key: "mo_ta", label: "Mô tả", visible: false },
  { key: "ma_ghi_chu", label: "Mẫu ghi chú", visible: false },
  { key: "vi_tri", label: "Vị trí", visible: true },
  { key: "hang_thanh_phan", label: "Hạng thành phần", visible: false },
  { key: "bao_hanh", label: "Bảo hành", visible: false },
  { key: "bao_tri_dinh_ky", label: "Bảo trì định kỳ", visible: false },
  { key: "supplier_id", label: "Nhà cung cấp", visible: true },
]);
const visibleColumns = computed(() =>
  columns.value.filter((col) => col.visible),
);
const renderValue = (item, col) => {
  const value = item[col.key];
  if (col.type === "currency") {
    return formatCurrency(value);
  }
  if (col.key === "supplier_id") {
    return item.supplier ? item.supplier.name : "N/A";
  }

  return value || "-";
};

const triggerFileInput = () => {
  fileInput.value.click();
};
const handleFileImport = async (event) => {
  const file = event.target.files[0];
  if (!file) return;
  const fileName = file.name.toLowerCase();
  if (
    !fileName.endsWith(".xlsx") &&
    !fileName.endsWith(".xls") &&
    !fileName.endsWith(".csv")
  ) {
    toast.error("Vui lòng chọn file Excel (.xlsx, .xls) hoặc .csv");
    return;
  }

  const formData = new FormData();
  formData.append("file", file);

  loading.value = true;
  try {
    const response = await importApi.importProducts(formData);
    toast.success(
      `Đã nhập thành công ${response.data.imported_count}} sản phẩm`,
    );
    event.target.value = "";
    fetchProducts();
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || "Có lỗi xảy ra khi nhập sản phẩm";
    toast.error(errorMessage);
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const fetchProducts = async () => {
  loading.value = true;
  try {
    const response = await productApi.getProductInTechnicalStock({
      page: pagination.current_page,
      search: search.value,
      loai_hang: filter.loai_hang,
      per_page: pagination.per_page || 15,
    });
    products.value = response.data.data.data || response.data;
    const { ...paginationMeta } = response.data.data;
    updatePagination(paginationMeta);
  } catch (error) {
    const errorMessage =
      error.response?.data?.error || "Có lỗi xảy ra khi tải dữ liệu sản phẩm";
    toast.error(errorMessage);
    console.error(error);
  } finally {
    loading.value = false;
  }
};

const approveToShelf = (id) => {
  confirmDialog.title = "Phê duyệt";
  confirmDialog.message = "Xác nhận sản phẩm đạt yêu cầu và cho phép bán?";
  confirmDialog.type = "warning";
  showConfirmDialog.value = true;
  confirmDialog.onConfirm = async () => {
    loading.value = true;
    try {
      const response = await productApi.moveToShelf(id);
      if (response.data.success) {
        fetchProducts();
        toast.success("Đã chuyển hàng lên kệ");
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.error || "Có lỗi khi duyệt lên kệ";
      toast.error(errorMessage);
    } finally {
      loading.value = false;
      showConfirmDialog.value = false;
      confirmDialog.onConfirm = null;
    }
  };
};

const viewModalCreateProduct = () => {
  showCreateModal.value = true;
};

const viewModalExportReceipt = () => {
  showExportReceiptModal.value = true;
};

const viewModalDetailProduct = (item) => {
  selectedProduct.value = item;
  showDetailModal.value = true;
};

const viewModalTrashProduct = () => {
  showModalTrashProduct.value = true;
};

const saveProduct = () => {
  showCreateModal.value = false;
  fetchProducts();
};

const saveExportReceipt = () => {
  showExportReceiptModal.value = false;
  fetchProducts();
};

const exportExcel = () => {
  const baseURL = process.env.VUE_APP_API_URL;
  const loaiHang = encodeURIComponent(filter.loai_hang || "");
  const khoKT = encodeURIComponent(filter.kho_ki_thuat || "");
  const url = `${baseURL}/export/products?loai_hang=${loaiHang}&kho_ki_thuat=${khoKT}`;

  window.open(url, "_blank");
};

const deleteProduct = (product) => {
  confirmDialog.title = "Xoá sản phẩm";
  confirmDialog.message = `Bạn có chắc muốn xoá sản phẩm mã ${product.ma_hang} không? Hành động này có thể khôi phục`;
  confirmDialog.type = "danger";
  showConfirmDialog.value = true;
  confirmDialog.onConfirm = async () => {
    try {
      await productApi.softDeleteProduct(product.id);
      toast.success("Đã chuyển sản phẩm vào thùng rác");
      const index = products.value.findIndex((p) => p.id === product.id);
      if (index !== -1) products.value.splice(index, 1);
    } catch (error) {
      const errorMsg = error.response?.data?.error || "Có lỗi xảy ra";
      toast.error(errorMsg);
    } finally {
      showConfirmDialog.value = false;
    }
  };
};

const { pagination, visiblePages, changePage, updatePagination } =
  usePagination(fetchProducts);

onMounted(fetchProducts);
</script>
